import React, { useMemo, useState } from 'react'
import { IoIosArrowRoundBack } from 'react-icons/io'
import { useHistory } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import useSWR from 'swr'
import Select from 'react-select'
import AsyncCreatableSelect from 'react-select/async-creatable'
import api, { fetcher } from '../../utils/api'
import {
  createOfflineOrder,
  formatOfflineOrderError,
  parseFarmerPhoneForInput,
} from '../../utils/offlineOrders'
import { trimFormValue } from '../../utils/formValues'
import { ErrorNotification, SuccessNotification } from '../../utils'

const trimField = { setValueAs: trimFormValue }

const CreateOfflineOrder = () => {
  const history = useHistory()
  const [itemType, setItemType] = useState('product')
  const [selectedProduct, setSelectedProduct] = useState(null)
  const [selectedCustomer, setSelectedCustomer] = useState(null)
  const [isDarkMode] = useState(() => localStorage.getItem('theme') === 'dark')

  const { data: farmersData } = useSWR('farmers/', fetcher)

  const { data: products, isLoading: loadingProducts } = useSWR(
    'service-orders/products/my_products/?is_active=true',
    fetcher
  )

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      customer_name: '',
      customer_phone: '',
      order_date: new Date().toISOString().split('T')[0],
      quantity: 1,
      notes: '',
    },
  })

  const filteredItems = useMemo(() => {
    if (!products) return []
    return products.filter(
      (p) => p.item_type?.toLowerCase() === itemType
    )
  }, [products, itemType])

  const selectOptions = filteredItems.map((item) => ({
    value: item.id,
    label: item.name,
    item,
  }))

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#1a1a2e' : '#fff',
      borderColor: state.isFocused ? '#FFB733' : isDarkMode ? '#374151' : '#d1d5db',
      boxShadow: state.isFocused ? '0 0 0 1px #FFB733' : 'none',
      minHeight: '38px',
      fontSize: '14px',
      fontWeight: 300,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#1a1a2e' : '#fff',
      zIndex: 20,
      fontSize: '14px',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected
        ? '#FFB733'
        : state.isFocused
          ? isDarkMode ? '#2d2d44' : '#f3f4f6'
          : 'transparent',
      color: state.isSelected ? '#000' : isDarkMode ? '#fff' : '#000',
      fontSize: '14px',
      fontWeight: 300,
    }),
    singleValue: (provided) => ({
      ...provided,
      color: isDarkMode ? '#fff' : '#000',
      fontSize: '14px',
      fontWeight: 300,
    }),
    input: (provided) => ({
      ...provided,
      color: isDarkMode ? '#fff' : '#000',
      fontSize: '14px',
    }),
    placeholder: (provided) => ({
      ...provided,
      color: isDarkMode ? '#9ca3af' : '#9ca3af',
      fontSize: '14px',
    }),
  }

  const defaultFarmerOptions = farmersData?.results?.map((farmer) => ({
    value: farmer.id,
    label: `${farmer.firstname} ${farmer.lastname}`,
    farmer,
  })) || []

  const loadFarmerOptions = async (inputValue) => {
    try {
      const response = await api.get('farmers/', {
        params: { search: inputValue, page_size: 20 },
      })
      return response.data.results.map((farmer) => ({
        value: farmer.id,
        label: `${farmer.firstname} ${farmer.lastname}`,
        farmer,
      }))
    } catch (error) {
      console.error('Error loading farmers:', error)
      return []
    }
  }

  const handleCustomerChange = (option) => {
    setSelectedCustomer(option)

    if (!option) {
      setValue('customer_name', '')
      setValue('customer_phone', '')
      clearErrors(['customer_name', 'customer_phone'])
      return
    }

    if (option.farmer) {
      const { firstname, lastname, phone } = option.farmer
      setValue('customer_name', `${firstname || ''} ${lastname || ''}`.trim())
      setValue('customer_phone', parseFarmerPhoneForInput(phone))
    } else {
      setValue('customer_name', (option.label || '').trim())
    }

    clearErrors(['customer_name', 'customer_phone'])
  }

  const handleCreateCustomer = (inputValue) => {
    const name = inputValue.trim()
    if (!name) return

    const option = {
      label: name,
      value: `custom:${name}`,
      __isNew__: true,
    }

    setSelectedCustomer(option)
    setValue('customer_name', name)
    clearErrors('customer_name')
  }

  const onSubmit = async (data) => {
    if (!selectedProduct) {
      ErrorNotification(`Please select a ${itemType === 'service' ? 'service' : 'product'}.`)
      return
    }

    const customerName = trimFormValue(data.customer_name)
    if (!customerName) {
      ErrorNotification('Customer name is required.')
      return
    }

    const farmerId = selectedCustomer?.farmer?.id ?? (
      typeof selectedCustomer?.value === 'number' ? selectedCustomer.value : undefined
    )

    try {
      const order = await createOfflineOrder({
        order_type: itemType,
        product_id: selectedProduct.id,
        quantity: itemType === 'product' ? Number(data.quantity) || 1 : undefined,
        customer_name: customerName,
        customer_phone: trimFormValue(data.customer_phone),
        order_date: data.order_date,
        notes: trimFormValue(data.notes) || '',
        farmer_id: farmerId,
      })

      SuccessNotification('Offline order recorded successfully.')
      history.push({
        pathname: '/admin/service-orders',
        state: { openOrderId: order.id, openAppointment: true },
      })
    } catch (err) {
      ErrorNotification(formatOfflineOrderError(err))
    }
  }

  const inputClass =
    'w-full bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 font-light px-3 text-sm focus:outline-none focus:ring-2 focus:ring-yellowcolor focus:border-transparent placeholder-gray-400'

  return (
    <div className="w-full lg:px-10 px-2 h-full py-6 lg:py-10">
      <div className="w-full max-w-2xl">
        <button
          type="button"
          onClick={() => history.push('/admin/service-orders')}
          className="mb-2 flex flex-row items-center"
        >
          <IoIosArrowRoundBack size={25} className="mr-2 dark:text-white" />
          <p className="text-sm font-light capitalize dark:text-white">Back</p>
        </button>

        <h2 className="text-2xl font-bold mb-2 dark:text-white">Record Offline Order</h2>
        <p className="text-sm font-light text-gray-500 dark:text-gray-400 mb-6">
          Log a sale or service completed outside the platform. You can schedule an appointment for the customer afterward.
        </p>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="bg-white dark:bg-dark rounded-lg overflow-hidden"
        >
          <div className="px-3 py-4 md:px-8 md:py-8 space-y-6">
            <div>
              <label className="block text-sm font-medium mb-2 dark:text-white">Order Type</label>
              <div className="flex gap-2">
                {['product', 'service'].map((type) => (
                  <button
                    key={type}
                    type="button"
                    onClick={() => {
                      setItemType(type)
                      setSelectedProduct(null)
                    }}
                    className={`flex-1 py-2 rounded-md text-sm font-light capitalize border transition-colors ${
                      itemType === type
                        ? 'bg-yellowcolor border-yellowcolor text-black'
                        : 'border-gray-300 dark:border-border-dark dark:text-white hover:border-yellowcolor'
                    }`}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 dark:text-white">
                Select {itemType === 'service' ? 'Service' : 'Product'}
                <span className="text-red-500">*</span>
              </label>
              {loadingProducts ? (
                <p className="text-sm font-light text-gray-500">Loading inventory...</p>
              ) : selectOptions.length === 0 ? (
                <p className="text-sm font-light text-amber-600 dark:text-amber-400">
                  No active {itemType === 'service' ? 'services' : 'products'} in your inventory.{' '}
                  <button
                    type="button"
                    onClick={() =>
                      history.push(
                        itemType === 'service' ? '/admin/add-service' : '/admin/add-stock'
                      )
                    }
                    className="underline"
                  >
                    Add one first
                  </button>
                </p>
              ) : (
                <Select
                  options={selectOptions}
                  value={
                    selectedProduct
                      ? { value: selectedProduct.id, label: selectedProduct.name }
                      : null
                  }
                  onChange={(opt) => setSelectedProduct(opt?.item || null)}
                  placeholder={`Choose a ${itemType}...`}
                  styles={selectStyles}
                  isClearable
                />
              )}
              {selectedProduct && (
                <p className="text-xs font-light text-gray-500 mt-1 dark:text-gray-400">
                  {itemType === 'product' && selectedProduct.unit_price != null
                    ? `Unit price: GH₵ ${Number(selectedProduct.unit_price).toLocaleString()}`
                    : selectedProduct.is_negotiable
                      ? 'Price is negotiable'
                      : ''}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 dark:text-white">
                Customer Name<span className="text-red-500">*</span>
              </label>
              <AsyncCreatableSelect
                cacheOptions
                defaultOptions={defaultFarmerOptions}
                loadOptions={loadFarmerOptions}
                value={selectedCustomer}
                onChange={handleCustomerChange}
                onCreateOption={handleCreateCustomer}
                placeholder="Search for a farmer or type a customer name..."
                styles={selectStyles}
                isClearable
                formatCreateLabel={(inputValue) => `Use "${inputValue.trim()}"`}
                isValidNewOption={(inputValue) => inputValue.trim().length > 0}
                noOptionsMessage={({ inputValue }) =>
                  inputValue
                    ? 'No farmers found — press Enter to use the name you typed'
                    : 'Type to search farmers or enter a customer name'
                }
              />
              <input
                type="hidden"
                {...register('customer_name', { required: 'Customer name is required' })}
              />
              {errors.customer_name && (
                <p className="text-red-500 text-xs mt-1">{errors.customer_name.message}</p>
              )}
              <p className="text-xs font-light text-gray-500 mt-1 dark:text-gray-400">
                Select a farmer to prefill their details, or type a name and press Enter if they are not in the list.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 dark:text-white">
                Phone Number <span className="text-gray-500 font-normal">(optional)</span>
              </label>
              <div className="flex items-center">
                <span className="mr-2 bg-white dark:bg-secondaryDark border dark:border-border-dark dark:text-white rounded-md py-2 pl-3 pr-4 flex items-center shrink-0 text-sm font-light">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/1/19/Flag_of_Ghana.svg"
                    alt="Ghana"
                    className="w-5 h-3 rounded-sm"
                  />
                  <span className="pl-2">+233</span>
                </span>
                <input
                  type="tel"
                  placeholder="eg. 544123456"
                  className={inputClass}
                  {...register('customer_phone', {
                    ...trimField,
                    validate: (value) => {
                      const trimmed = trimFormValue(value)
                      if (!trimmed) return true
                      return /^[0-9]{9,10}$/.test(trimmed) || 'Enter a valid phone number'
                    },
                  })}
                />
              </div>
              {errors.customer_phone && (
                <p className="text-red-500 text-xs mt-1">{errors.customer_phone.message}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium mb-1 dark:text-white">
                  Order Date<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  className={inputClass}
                  {...register('order_date', { required: 'Order date is required' })}
                />
                {errors.order_date && (
                  <p className="text-red-500 text-xs mt-1">{errors.order_date.message}</p>
                )}
              </div>

              {itemType === 'product' && (
                <div>
                  <label className="block text-sm font-medium mb-1 dark:text-white">Quantity</label>
                  <input
                    type="number"
                    min={1}
                    className={inputClass}
                    {...register('quantity', {
                      min: { value: 1, message: 'Minimum quantity is 1' },
                    })}
                  />
                  {errors.quantity && (
                    <p className="text-red-500 text-xs mt-1">{errors.quantity.message}</p>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 dark:text-white">Notes</label>
              <textarea
                rows={3}
                placeholder="Optional notes about this order"
                className={inputClass}
                {...register('notes', trimField)}
              />
            </div>
          </div>

          <div className="flex flex-col-reverse sm:flex-row border-t border-gray-200 dark:border-border-dark p-4 gap-3 sm:justify-end">
            <button
              type="button"
              onClick={() => history.push('/admin/service-orders')}
              className="w-full sm:w-auto px-6 text-sm py-2 bg-white dark:bg-dark border dark:border-border-dark dark:text-white text-black rounded-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !selectedProduct}
              className="w-full sm:w-auto px-6 text-sm py-2 bg-yellowcolor hover:bg-yellow-400 text-black rounded-lg disabled:opacity-60"
            >
              {isSubmitting ? 'Saving...' : 'Save Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateOfflineOrder
